import React, { useState } from 'react';

import { Select, FormLabel, FormControl, Stack, Skeleton, Button, Tooltip, HStack, Icon } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { FiDownload } from 'react-icons/fi';

import { V4TransformedLciaMethod } from 'hooks/data/datasets/V4/lcia/types';
import useV4LciaScores from 'hooks/data/datasets/V4/lcia/useV4LciaScores';
import useV4Dataset from 'hooks/data/datasets/V4/useV4Dataset';
import useVersionInfo from 'hooks/useVersionInfo';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { downloadCsvContent } from 'utilities/helpers/downloads';

import V4ImpactAssessmentTable from './components/v4ImpactAssessmentTable';

type Props = { methodList?: V4TransformedLciaMethod[]; uprId: string; isLoading?: boolean };

const V4ImpactAssessmentDetails = ({ methodList, uprId, isLoading }: Props) => {
  const [selectedMethod, setSelectedMethod] = useState<V4TransformedLciaMethod | null>({
    method_id: '167c6bac-650c-4413-adfe-9f93b2b22488',
    method_name: 'IPCC 2021',
  });

  const { data: V4LciaScores, isLoading: isV4Loading } = useV4LciaScores(uprId, selectedMethod?.method_id ?? '');
  const { data: datasetData } = useV4Dataset(uprId);
  const { ver } = useVersionInfo();

  const hasData = !!V4LciaScores && V4LciaScores.length > 0;

  const handleExport = () => {
    if (!V4LciaScores) return;
    const header = 'Impact Category,Indicator,Impact Score,Unit';
    const rows = V4LciaScores.map((s) =>
      [s.category, s.indicator, s.amount, s.unit].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const datasetName = (datasetData?.name ?? 'dataset').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const filename = `impact-scores_${datasetName}_${ver}.csv`;
    downloadCsvContent(filename, csv);
    mixpanel.track(MIXPANEL_EVENTS.IMPACT_SCORES_EXPORTED, {
      dataset_id: uprId,
      row_count: V4LciaScores.length,
    });
  };

  return (
    <Stack spacing={4}>
      <HStack justify="space-between" align="flex-end">
        <FormControl size="sm" flex={1}>
          <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'}>
            <FormLabel id="method" fontSize={'sm'}>
              LCIA Method
            </FormLabel>
          </Skeleton>
          <Skeleton isLoaded={!isLoading}>
            <Select
              isDisabled={!methodList?.length}
              value={selectedMethod?.method_id}
              onChange={(e) => setSelectedMethod(methodList?.find((m) => m.method_id === e.target.value) || null)}
            >
              {methodList?.map((m) => {
                return (
                  <option key={m.method_id} value={m.method_id}>
                    {m.method_name}
                  </option>
                );
              })}
            </Select>
          </Skeleton>
        </FormControl>
        <Tooltip label="No data available" isDisabled={hasData} hasArrow>
          <Button
            leftIcon={<Icon as={FiDownload} />}
            variant="ghost"
            size="md"
            isDisabled={!hasData}
            onClick={handleExport}
            flexShrink={0}
          >
            Export Impact Scores
          </Button>
        </Tooltip>
      </HStack>
      {selectedMethod && (
        <V4ImpactAssessmentTable
          selectedMethod={selectedMethod}
          uprId={uprId}
          isLoading={isLoading}
          isV4Loading={isV4Loading}
          V4LciaScores={V4LciaScores}
        />
      )}
    </Stack>
  );
};

export default V4ImpactAssessmentDetails;
